import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@app/../environments/environment';
import { TranslationService } from '@app/core/_base/layout';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map, switchMap } from 'rxjs/operators';

import {
  ContactApiResponse,
  ContactDepartment,
  ContactInquiryRequest,
  ContactInquiryResponse,
  ContactLanguageCode,
  ContactPerson,
  ContactPersonCollection,
  ContactSieveParams,
  MyInquiry,
  MyInquiryCollection
} from '@app/model/contact.model';

/**
 * ContactDataService
 * ------------------
 * Wrapper for the Public Client APIs of the Contact module
 * (see contact-api-integration-guide.md, section 4).
 *
 * Endpoint base: /api/contact
 *  - GET    /api/contact/departments
 *  - GET    /api/contact/persons
 *  - GET    /api/contact/areaManagers
 *  - GET    /api/contact/persons/{id}
 *  - POST   /api/contact/inquiry
 *
 * Automatically applies the current language from TranslationService.
 */
@Injectable({
  providedIn: 'root'
})
export class ContactDataService {
  private readonly apiUrl = environment.backendhost + '/api/contact';

  constructor(
    private http: HttpClient,
    private translationService: TranslationService
  ) {}

  /** Observable of the current language mapped to the backend code ('vi' | 'en' | 'zh'). */
  get languageCode$(): Observable<ContactLanguageCode> {
    return this.translationService.language$.pipe(
      map((language) => this.mapApiLanguage(language)),
      distinctUntilChanged()
    );
  }

  /**
   * 4.1. Get the list of Departments / Tabs.
   * GET /api/contact/departments?languageCode=
   */
  getDepartments(languageCode?: ContactLanguageCode): Observable<ContactDepartment[]> {
    const source$ = languageCode
      ? this.loadDepartments(languageCode)
      : this.languageCode$.pipe(switchMap((lang) => this.loadDepartments(lang)));

    return source$.pipe(map((response) => (response && response.data ? response.data : [])));
  }

  /**
   * 4.2. Get the list of contact persons (with Sieve pagination/filtering).
   * GET /api/contact/persons?filters=&languageCode=&page=&pageSize=&sorts=
   */
  getPersons(params: ContactSieveParams = {}): Observable<ContactPersonCollection> {
    const languageCode = params.languageCode;
    const source$ = languageCode
      ? this.loadPersons(languageCode, params)
      : this.languageCode$.pipe(switchMap((lang) => this.loadPersons(lang, params)));

    return source$.pipe(
      map((response) => {
        const data: any = response ? response.data : null;
        if (!data) {
          return { collection: [], total: 0, pagesize: params.pageSize || 20, pageIndex: params.page || 1 };
        }
        // Backend may return a paginated object or a flat array.
        if (Array.isArray(data)) {
          return { collection: data, total: data.length, pagesize: params.pageSize || 20, pageIndex: params.page || 1 };
        }
        return {
          collection: Array.isArray(data.collection) ? data.collection : [],
          total: typeof data.total === 'number' ? data.total : (data.collection || []).length,
          pagesize: typeof data.pagesize === 'number' ? data.pagesize : params.pageSize || 20,
          pageIndex: typeof data.pageIndex === 'number' ? data.pageIndex : params.page || 1
        };
      })
    );
  }

  /**
   * 4.3. Quickly get the list of Area Managers.
   * Convenience endpoint that auto-applies departmentCode==area-managers.
   */
  getAreaManagers(params: ContactSieveParams = {}): Observable<ContactPersonCollection> {
    const languageCode = params.languageCode;
    const source$ = languageCode
      ? this.loadAreaManagers(languageCode, params)
      : this.languageCode$.pipe(switchMap((lang) => this.loadAreaManagers(lang, params)));

    return source$.pipe(
      map((response) => {
        const data: any = response ? response.data : null;
        if (!data) {
          return { collection: [], total: 0, pagesize: params.pageSize || 20, pageIndex: params.page || 1 };
        }
        if (Array.isArray(data)) {
          return { collection: data, total: data.length, pagesize: params.pageSize || 20, pageIndex: params.page || 1 };
        }
        return {
          collection: Array.isArray(data.collection) ? data.collection : [],
          total: typeof data.total === 'number' ? data.total : (data.collection || []).length,
          pagesize: typeof data.pagesize === 'number' ? data.pagesize : params.pageSize || 20,
          pageIndex: typeof data.pageIndex === 'number' ? data.pageIndex : params.page || 1
        };
      })
    );
  }

  /**
   * 4.4. Detail of a single Person.
   * GET /api/contact/persons/{id}?languageCode=
   */
  getPerson(id: string, languageCode?: ContactLanguageCode): Observable<ContactPerson | null> {
    const source$ = languageCode
      ? this.loadPerson(id, languageCode)
      : this.languageCode$.pipe(switchMap((lang) => this.loadPerson(id, lang)));

    return source$.pipe(map((response) => (response && response.data ? response.data : null)));
  }

  /**
   * 4.5. Submit the contact form (Submit Inquiry).
   * POST /api/contact/inquiry
   */
  submitInquiry(payload: ContactInquiryRequest): Observable<ContactInquiryResponse> {
    return this.http.post<ContactInquiryResponse>(this.apiUrl + '/inquiry', payload);
  }

  /**
   * 6.1. Get the list of "My Inquiries" (logged-in customer's own requests).
   * GET /api/contact/my-inquiries?filters=&page=&pageSize=&sorts=
   * Requires an Authorization header (handled by the auth interceptor).
   */
  getMyInquiries(params: ContactSieveParams = {}): Observable<MyInquiryCollection> {
    return this.http
      .get<ContactApiResponse<MyInquiryCollection>>(this.apiUrl + '/my-inquiries', {
        params: this.buildSieveParamsNoLang(params)
      })
      .pipe(
        map((response) => {
          const data: any = response ? response.data : null;
          if (!data) {
            return { collection: [], total: 0, pagesize: params.pageSize || 20, pageIndex: params.page || 1 };
          }
          if (Array.isArray(data)) {
            return { collection: data, total: data.length, pagesize: params.pageSize || 20, pageIndex: params.page || 1 };
          }
          return {
            collection: Array.isArray(data.collection) ? data.collection : [],
            total: typeof data.total === 'number' ? data.total : (data.collection || []).length,
            pagesize: typeof data.pagesize === 'number' ? data.pagesize : params.pageSize || 20,
            pageIndex: typeof data.pageIndex === 'number' ? data.pageIndex : params.page || 1
          };
        })
      );
  }

  /**
   * 6.2. Get the detail of one "My Inquiry" by id.
   * GET /api/contact/my-inquiries/{id}
   * Requires an Authorization header. The backend enforces ownership
   * (email/phone must match the logged-in account).
   */
  getMyInquiryDetail(id: string): Observable<MyInquiry | null> {
    return this.http
      .get<ContactApiResponse<MyInquiry>>(this.apiUrl + '/my-inquiries/' + encodeURIComponent(id))
      .pipe(map((response) => (response && response.data ? response.data : null)));
  }

  // ---------------------- Private helpers ----------------------

  private loadDepartments(languageCode: ContactLanguageCode): Observable<ContactApiResponse<ContactDepartment[]>> {
    const params = new HttpParams().set('languageCode', languageCode);
    return this.http.get<ContactApiResponse<ContactDepartment[]>>(this.apiUrl + '/departments', { params });
  }

  private loadPersons(
    languageCode: ContactLanguageCode,
    params: ContactSieveParams
  ): Observable<ContactApiResponse<ContactPersonCollection>> {
    return this.http.get<ContactApiResponse<ContactPersonCollection>>(this.apiUrl + '/persons', {
      params: this.buildSieveParams(languageCode, params)
    });
  }

  private loadAreaManagers(
    languageCode: ContactLanguageCode,
    params: ContactSieveParams
  ): Observable<ContactApiResponse<ContactPersonCollection>> {
    return this.http.get<ContactApiResponse<ContactPersonCollection>>(this.apiUrl + '/areaManagers', {
      params: this.buildSieveParams(languageCode, params)
    });
  }

  private loadPerson(
    id: string,
    languageCode: ContactLanguageCode
  ): Observable<ContactApiResponse<ContactPerson>> {
    const params = new HttpParams().set('languageCode', languageCode);
    return this.http.get<ContactApiResponse<ContactPerson>>(this.apiUrl + '/persons/' + encodeURIComponent(id), {
      params
    });
  }

  private buildSieveParams(languageCode: ContactLanguageCode, params: ContactSieveParams): HttpParams {
    let query = new HttpParams().set('languageCode', languageCode);
    if (params.filters !== undefined && params.filters !== null && params.filters !== '') {
      query = query.set('filters', params.filters);
    }
    if (params.sorts !== undefined && params.sorts !== null && params.sorts !== '') {
      query = query.set('sorts', params.sorts);
    }
    if (params.page !== undefined && params.page !== null) {
      query = query.set('page', params.page.toString());
    }
    if (params.pageSize !== undefined && params.pageSize !== null) {
      query = query.set('pageSize', params.pageSize.toString());
    }
    return query;
  }

  /** Same as buildSieveParams but without the languageCode parameter (used by my-inquiries). */
  private buildSieveParamsNoLang(params: ContactSieveParams): HttpParams {
    let query = new HttpParams();
    if (params.filters !== undefined && params.filters !== null && params.filters !== '') {
      query = query.set('filters', params.filters);
    }
    if (params.sorts !== undefined && params.sorts !== null && params.sorts !== '') {
      query = query.set('sorts', params.sorts);
    }
    if (params.page !== undefined && params.page !== null) {
      query = query.set('page', params.page.toString());
    }
    if (params.pageSize !== undefined && params.pageSize !== null) {
      query = query.set('pageSize', params.pageSize.toString());
    }
    return query;
  }

  /** Maps the app language code to the backend code ('vi' | 'en' | 'zh'). */
  private mapApiLanguage(language: string): ContactLanguageCode {
    const current = (language || '').toLowerCase();
    if (current === 'ch' || current === 'cn' || current === 'zh') {
      return 'zh';
    }
    if (current === 'en') {
      return 'en';
    }
    return 'vi';
  }
}
